import { useState, useEffect, useCallback } from 'react';
import { Loader, Plus, Pencil, Trash2, Gift, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import {
  PartnershipIncentive,
  INCENTIVE_TYPES,
  IncentiveType,
  INCENTIVE_STATUSES,
  IncentiveStatus,
  CreateIncentiveRequest,
  UpdateIncentiveRequest,
} from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface PartnershipIncentivesSectionProps {
  partnershipId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

const TYPE_COLORS: Record<IncentiveType, string> = {
  free_server: 'badge-purple',
  monetization_concession: 'badge-amber',
  revenue_share: 'badge-success',
  marketing: 'badge-info',
  custom: 'badge-neutral',
};

const STATUS_COLORS: Record<IncentiveStatus, string> = {
  active: 'badge-success',
  pending: 'badge-amber',
  expired: 'badge-warning',
  revoked: 'badge-danger',
};

export function PartnershipIncentivesSection({
  partnershipId,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: PartnershipIncentivesSectionProps) {
  const [incentives, setIncentives] = useState<PartnershipIncentive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadIncentives = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getIncentivesForPartnership(partnershipId);
      setIncentives(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incentives');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    loadIncentives();
  }, [loadIncentives]);

  const handleCreate = async (formData: CreateIncentiveRequest) => {
    if (!canCreate || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.createIncentive(partnershipId, formData);
      setShowCreate(false);
      await loadIncentives();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create incentive');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (incentiveId: string, data: UpdateIncentiveRequest) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.updateIncentive(incentiveId, data);
      setEditingId(null);
      await loadIncentives();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update incentive');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (incentiveId: string) => {
    if (!canDelete || deletingId) return;
    const confirmed = window.confirm('Are you sure you want to delete this incentive?');
    if (!confirmed) return;
    setDeletingId(incentiveId);
    try {
      await partnershipsApiService.deleteIncentive(incentiveId);
      await loadIncentives();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete incentive');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (ts: number | null) => {
    if (!ts) return '—';
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return format(new Date(ms), 'MMM d, yyyy');
  };

  const toDatetimeLocal = (ts: number | null) => {
    if (!ts) return '';
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return format(new Date(ms), "yyyy-MM-dd'T'HH:mm");
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-24" />
          <SkeletonLoader className="h-9 w-32 rounded-lg" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="subcard p-3 space-y-2">
            <div className="flex items-center gap-2">
              <SkeletonLoader className="h-5 w-20 rounded-full" />
              <SkeletonLoader className="h-5 w-14 rounded-full" />
              <SkeletonLoader className="h-4 w-40" />
            </div>
            <SkeletonLoader className="h-3 w-full" />
            <SkeletonLoader className="h-3 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load incentives"
        description={error}
        action={
          <button type="button" onClick={loadIncentives} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Incentives"
        icon={Gift}
        count={incentives.length}
        actions={
          canCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
              aria-expanded={showCreate}
            >
              <Plus className="w-4 h-4" />
              Add Incentive
            </button>
          ) : undefined
        }
      />

      {showCreate && canCreate && (
        <IncentiveCreateForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          submitting={submitting}
        />
      )}

      <div className="space-y-2">
        {incentives.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={Gift}
            title="No incentives yet"
            description="Record the perks you have agreed to provide, with dates and a status so the team knows what is still owed."
            action={canCreate && !showCreate ? <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Add the first incentive</button> : undefined}
          />
        ) : (
          incentives.map((incentive) => (
            <div
              key={incentive.id}
              className="subcard p-3 space-y-2"
            >
              {editingId === incentive.id && canEdit ? (
                <IncentiveEditForm
                  incentive={incentive}
                  onSave={(data) => handleUpdate(incentive.id, data)}
                  onCancel={() => setEditingId(null)}
                  submitting={submitting}
                  toDatetimeLocal={toDatetimeLocal}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm font-medium text-white">{incentive.title}</span>
                      <span className={cn('badge', TYPE_COLORS[incentive.type])}>
                        {incentive.type.replace(/_/g, ' ')}
                      </span>
                      <span className={cn('badge badge-dot', STATUS_COLORS[incentive.status])}>
                        {incentive.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setEditingId(incentive.id)}
                          className="btn-icon btn-icon-sm"
                          title="Edit"
                          aria-label="Edit incentive"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(incentive.id)}
                          disabled={deletingId === incentive.id}
                          className={cn(
                            'btn-icon btn-icon-sm text-red-400 hover:text-red-300',
                            deletingId === incentive.id && 'opacity-50 cursor-not-allowed'
                          )}
                          title="Delete"
                          aria-label="Delete incentive"
                        >
                          {deletingId === incentive.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {incentive.description && (
                    <div className="text-sm text-glass-300">
                      <MarkdownRenderer content={incentive.description} className="text-sm" />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-glass-500 tabular-nums">
                    <span>
                      {formatDate(incentive.start_date)} – {formatDate(incentive.end_date)}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function IncentiveCreateForm({
  onSubmit,
  onCancel,
  submitting,
}: {
  onSubmit: (data: CreateIncentiveRequest) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [type, setType] = useState<IncentiveType>('custom');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<IncentiveStatus>('pending');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      type,
      title: title.trim(),
      description: description.trim(),
      value: value.trim() || undefined,
      status,
      start_date: startDate ? new Date(startDate).getTime() : null,
      end_date: endDate ? new Date(endDate).getTime() : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="subcard p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as IncentiveType)}
            className="glass-select w-full capitalize"
          >
            {INCENTIVE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as IncentiveStatus)}
            className="glass-select w-full capitalize"
          >
            {INCENTIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Incentive title"
          className="glass-input w-full"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Markdown supported"
          rows={3}
          className="glass-input w-full resize-none"
          required
        />
      </div>
      <details className="subcard p-3">
        <summary className="text-sm text-glass-300 cursor-pointer min-h-[32px]">Advanced incentive data (optional)</summary>
        <p className="text-xs text-glass-400 mb-2">Use this only when the incentive needs structured data. The title and description are enough for a normal agreement.</p>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Value (JSON)</label>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder='{"key": "value"}'
          rows={2}
          className="glass-input w-full resize-none font-mono"
        />
      </details>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Start Date</label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="glass-input w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">End Date</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="glass-input w-full"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create
        </button>
      </div>
    </form>
  );
}

function IncentiveEditForm({
  incentive,
  onSave,
  onCancel,
  submitting,
  toDatetimeLocal,
}: {
  incentive: PartnershipIncentive;
  onSave: (data: UpdateIncentiveRequest) => void;
  onCancel: () => void;
  submitting: boolean;
  toDatetimeLocal: (ts: number | null) => string;
}) {
  const [type, setType] = useState<IncentiveType>(incentive.type);
  const [title, setTitle] = useState(incentive.title);
  const [description, setDescription] = useState(incentive.description);
  const [value, setValue] = useState(incentive.value || '');
  const [status, setStatus] = useState<IncentiveStatus>(incentive.status);
  const [startDate, setStartDate] = useState(toDatetimeLocal(incentive.start_date));
  const [endDate, setEndDate] = useState(toDatetimeLocal(incentive.end_date));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      type,
      title: title.trim(),
      description: description.trim(),
      value: value.trim() || null,
      status,
      start_date: startDate ? new Date(startDate).getTime() : null,
      end_date: endDate ? new Date(endDate).getTime() : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as IncentiveType)}
            className="glass-select w-full capitalize"
          >
            {INCENTIVE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as IncentiveStatus)}
            className="glass-select w-full capitalize"
          >
            {INCENTIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="glass-input w-full"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="glass-input w-full resize-none"
          required
        />
      </div>
      <details className="subcard p-3">
        <summary className="text-sm text-glass-300 cursor-pointer min-h-[32px]">Advanced incentive data{value ? ' (saved)' : ' (optional)'}</summary>
        <p className="text-xs text-glass-400 mb-2">Existing data is preserved when you save the other fields.</p>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Value (JSON)</label>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none font-mono"
        />
      </details>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Start Date</label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="glass-input w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">End Date</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="glass-input w-full"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary min-w-[5rem]">
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}
